import { DEFAULT_EXPECTED_DOCUMENT_TYPES } from "./documentIntake";

export const MONTHLY_DECLARATION_TYPES = Object.freeze([
  { id: "vat", label: "VAT declaration", cadence: "Monthly" },
  { id: "payroll", label: "Payroll declaration", cadence: "Monthly" },
  { id: "withholding", label: "Withholding declaration", cadence: "Monthly" },
  { id: "quarterly-income-tax", label: "Quarterly income tax", cadence: "Quarterly" },
  { id: "annual-return", label: "Annual declaration", cadence: "Annual" },
]);

export const MONTHLY_DECLARATION_IDS = MONTHLY_DECLARATION_TYPES.map((item) => item.id);

const usableDocument = (item) => !item.archived && !["Duplicate", "Unsupported", "Failed"].includes(item.status);
const reviewedDocument = (item) => ["Ready", "Accepted"].includes(item.status);

export const suggestedDeclarationsForBusiness = (business = {}) => business.vatStatus === "registered" ? ["vat"] : [];

export const monthlyCloseDraft = ({ expectation, business } = {}) => {
  const declarationTypes = expectation
    ? [...(expectation.declarationTypes || [])]
    : suggestedDeclarationsForBusiness(business);
  return {
    documentTypes: [...(expectation?.documentTypes?.length ? expectation.documentTypes : DEFAULT_EXPECTED_DOCUMENT_TYPES)],
    completedDocumentTypes: (expectation?.completedDocumentTypes || []).filter((type) => expectation?.documentTypes?.includes(type)),
    declarationTypes,
    completedDeclarations: (expectation?.completedDeclarations || []).filter((type) => declarationTypes.includes(type)),
    deadline: expectation?.deadline || "",
  };
};

export const monthlyCloseStatus = ({ items = [], expectation, businessId, period } = {}) => {
  const documentTypes = expectation?.documentTypes || [];
  const completedDocumentTypes = new Set(expectation?.completedDocumentTypes || []);
  const declarationTypes = expectation?.declarationTypes || [];
  const completedDeclarations = new Set(expectation?.completedDeclarations || []);
  const documents = documentTypes.map((documentType) => {
    const matches = items.filter((item) => item.businessId === businessId
      && item.period === period
      && item.documentType === documentType
      && usableDocument(item));
    const reviewedCount = matches.filter(reviewedDocument).length;
    const received = matches.length > 0;
    const reviewed = reviewedCount > 0;
    return {
      documentType,
      count: matches.length,
      reviewedCount,
      received,
      reviewed,
      completed: received && reviewed && completedDocumentTypes.has(documentType),
      itemIds: matches.map((item) => item.id),
    };
  });
  const declarations = declarationTypes.map((id) => ({
    id,
    label: MONTHLY_DECLARATION_TYPES.find((item) => item.id === id)?.label || id,
    cadence: MONTHLY_DECLARATION_TYPES.find((item) => item.id === id)?.cadence || "Period based",
    completed: completedDeclarations.has(id),
  }));
  const missingDocuments = documents.filter((item) => !item.received);
  const documentsNeedingReview = documents.filter((item) => item.received && !item.reviewed);
  const documentsNeedingConfirmation = documents.filter((item) => item.reviewed && !item.completed);
  const pendingDeclarations = declarations.filter((item) => !item.completed);
  const completedSteps = documents.filter((item) => item.completed).length + declarations.filter((item) => item.completed).length;
  const totalSteps = documents.length + declarations.length;
  const configured = Boolean(expectation && documentTypes.length);
  const complete = configured
    && totalSteps > 0
    && missingDocuments.length === 0
    && documentsNeedingReview.length === 0
    && documentsNeedingConfirmation.length === 0
    && pendingDeclarations.length === 0;
  let state = "setup";
  let label = "Set up checklist";
  if (configured && complete) {
    state = "complete";
    label = "Month complete";
  } else if (configured && missingDocuments.length) {
    state = "missing";
    label = `${missingDocuments.length} document type${missingDocuments.length === 1 ? "" : "s"} missing`;
  } else if (configured && documentsNeedingReview.length) {
    state = "review";
    label = `${documentsNeedingReview.length} document type${documentsNeedingReview.length === 1 ? "" : "s"} to review`;
  } else if (configured && documentsNeedingConfirmation.length) {
    state = "confirm";
    label = `${documentsNeedingConfirmation.length} document type${documentsNeedingConfirmation.length === 1 ? "" : "s"} to confirm`;
  } else if (configured && pendingDeclarations.length) {
    state = "declarations";
    label = `${pendingDeclarations.length} declaration${pendingDeclarations.length === 1 ? "" : "s"} pending`;
  }
  return {
    configured,
    complete,
    state,
    label,
    documents,
    declarations,
    missingDocuments,
    documentsNeedingReview,
    documentsNeedingConfirmation,
    pendingDeclarations,
    completedSteps,
    totalSteps,
  };
};
